// Which map does this job need, and where do I get it?
//
// A ticket carries `epochPrefix8` — the first 8 bytes of the issuing
// graph's `sourceHash`. That says *which* graph, unambiguously and
// unforgeably, because the epoch is a SHA-256 over the graph's own
// content. What it does not say is where to fetch it, which is why a
// driver whose phone held the wrong region got a hex mismatch and a dead
// end instead of a job.
//
// So the operator publishes a catalogue and the phone matches on epoch:
// the ticket says which, the catalogue says where. Nothing new goes on
// the wire and no new trust is extended — the catalogue is served by the
// same operator who issued the ticket.
//
// **The load-bearing rule is `assertGraphEpoch`.** Whatever chose the URL
// — a catalogue, a setting, a field in a future ticket — the graph that
// comes back must be checked against the epoch the ticket named *before*
// it is used. The epoch is not decoration: it defines the routing truth a
// driver follows and the topic namespace a run publishes on. Skip the
// check and "switch map automatically" becomes a way to point somebody's
// routing at anything. With the check, a wrong or hostile URL can only
// serve the genuine graph or fail.

/**
 * A dataset id, as the epoch the mesh actually rendezvouses on.
 *
 * `sourceHash` is a SHA-256 over the graph's node coordinates, edge
 * topology and edge weights, so **one new driveway changes it**. That is
 * exactly right for "is this the same map" and exactly wrong for "can we
 * find each other": the epoch is also the topic namespace (§4.2), and a
 * public index rebuilt weekly from Geofabrik extracts would rename every
 * running job's channel every week.
 *
 * So a dataset — `quebec/car` — names the *region and profile*, which do
 * not change when the roads in them do. Hashing it produces a 64-char hex
 * string indistinguishable in shape from a `sourceHash`, which is what
 * lets it be handed straight to the engine's `epochHex` without touching
 * the protocol.
 *
 * The prefix is load-bearing: without it a dataset id and a content hash
 * live in the same space, and a graph could be addressed by either. They
 * mean different things and must never be confusable.
 */
const DATASET_NAMESPACE = "wayfind-dataset-v1:";

/**
 * The stable epoch for a dataset. Every party derives it from the same
 * string, so a dispatcher, a driver and a recipient agree without anyone
 * publishing anything — and keep agreeing across a rebuild.
 */
export async function datasetEpoch(datasetId: string): Promise<string> {
  const name = String(datasetId ?? "").trim().toLowerCase();
  if (!name) throw new Error("A dataset id cannot be empty.");
  const bytes = new TextEncoder().encode(DATASET_NAMESPACE + name);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, "0")).join("");
}

/** What a loaded graph can be identified by. */
export interface GraphIdentity {
  /** SHA-256 over the graph's contents. Changes on every rebuild. */
  sourceHash: string;
  /**
   * The stable epoch, when this session was opened against a named
   * dataset. Null means the graph is identified by its contents alone,
   * which is the old behaviour and still correct for a graph an operator
   * built and pinned themselves.
   */
  datasetEpoch: string | null;
}

/** One published graph: what it is, and where it lives. */
export interface GraphEntry {
  /** First 8 bytes of the graph's sourceHash, lowercase hex (16 chars). */
  epoch: string;
  /** Human name, for telling somebody which map they are missing. */
  name: string;
  /** Base URL the engine opens. */
  url: string;
  /**
   * The dataset this entry is a build of — `quebec/car`. Null for a
   * catalogue that predates datasets, whose entries are identified by
   * content alone.
   *
   * It lives here rather than in each app's configuration because the
   * dataset and the URL have to agree: pointing at a graph while naming
   * somebody else's dataset would put this device on a topic no run
   * publishes to, and nothing about that failure looks like a typo.
   */
  dataset: string | null;
}

/** What happened when a ticket's epoch was looked up. */
export type GraphChoice =
  /** Found it. Load this, then `assertGraphEpoch` before using it. */
  | { kind: "found"; entry: GraphEntry }
  /**
   * No published graph for this epoch. Distinct from a broken catalogue:
   * the job is simply somewhere this operator has not published a map,
   * and the driver deserves that sentence rather than a hex string.
   */
  | { kind: "unpublished"; epoch: string }
  /** The catalogue itself could not be read. */
  | { kind: "unavailable"; reason: string };

const EPOCH = /^[0-9a-f]{16}$/u;

/** Normalises an epoch to the 16-char lowercase hex the catalogue keys on. */
export function epochKey(value: string): string | null {
  const text = String(value ?? "").trim().toLowerCase().replace(/^0x/u, "");
  if (text.length < 16) return null;
  const prefix = text.slice(0, 16);
  return EPOCH.test(prefix) ? prefix : null;
}

/** Parses a catalogue document, dropping entries that are not usable. */
export function readCatalogue(document: unknown): GraphEntry[] {
  const rows = Array.isArray(document)
    ? document
    : Array.isArray((document as { graphs?: unknown })?.graphs)
      ? (document as { graphs: unknown[] }).graphs
      : [];
  const out: GraphEntry[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const entry = row as Record<string, unknown>;
    const epoch = epochKey(String(entry.epoch ?? ""));
    const url = String(entry.url ?? "").trim();
    // An entry with no epoch cannot be matched and an entry with no URL
    // cannot be loaded. Either way it is noise, and silently keeping it
    // would let a typo look like an unpublished region.
    if (!epoch || !url) continue;
    out.push({
      epoch,
      name: String(entry.name ?? "").trim() || epoch,
      url,
      dataset: String(entry.dataset ?? "").trim().toLowerCase() || null
    });
  }
  return out;
}

/** Picks the graph a ticket needs out of a catalogue. */
export function chooseGraph(ticketEpoch: string, catalogue: GraphEntry[]): GraphChoice {
  const wanted = epochKey(ticketEpoch);
  if (!wanted) return { kind: "unavailable", reason: "That ticket does not name a map." };
  const entry = catalogue.find(candidate => candidate.epoch === wanted);
  return entry ? { kind: "found", entry } : { kind: "unpublished", epoch: wanted };
}

/**
 * Refuses a graph that is not the one the ticket named.
 *
 * Throws rather than returning false: every caller of this is about to
 * route a driver and publish a run on the resulting topic, and a boolean
 * is something a caller can forget to read.
 */
export function assertGraphEpoch(
  loaded: string | GraphIdentity,
  ticketEpoch: string
): void {
  const identity: GraphIdentity = typeof loaded === "string"
    ? { sourceHash: loaded, datasetEpoch: null }
    : loaded;
  const wanted = epochKey(ticketEpoch);
  const content = epochKey(identity.sourceHash);
  const dataset = identity.datasetEpoch ? epochKey(identity.datasetEpoch) : null;
  if (!wanted || (!content && !dataset)) {
    throw new Error("A graph and a ticket must both name an epoch before they can be compared.");
  }

  // A ticket minted against a dataset names the region, not the build, so
  // it matches every build of that region — which is the entire point:
  // the job survives the Tuesday rebuild instead of being refused by the
  // driver who is standing at the door.
  if (dataset && wanted === dataset) return;

  // A ticket minted before this device knew about datasets still names a
  // content hash. Honouring it keeps old tickets working through the
  // change rather than stranding them on the way out of the old scheme.
  if (content && wanted === content) return;

  // Naming neither is a wrong map. Saying *which* kind of wrong matters:
  // "you are on the right region but a build this job predates" is a
  // resync, and "this is a different region entirely" is not.
  if (dataset) {
    throw new Error(
      `That map is not the one this job was made with (job ${wanted}, map ${dataset}). `
      + "Refusing it rather than routing on the wrong map."
    );
  }
  throw new Error(
    `That map is not the one this job was made with (job ${wanted}, map ${content}). `
    + "Refusing it rather than routing on the wrong map."
  );
}

/**
 * Fetches a catalogue. Never throws — an unreachable catalogue is a
 * degraded state the app has a sentence for, not a crash.
 */
export async function fetchCatalogue(
  url: string,
  fetchImpl: typeof fetch = fetch
): Promise<GraphEntry[] | { error: string }> {
  try {
    const response = await fetchImpl(url, { cache: "no-cache" });
    if (!response.ok) return { error: `The map catalogue answered ${response.status}.` };
    return readCatalogue(await response.json());
  } catch (error) {
    return { error: error instanceof Error ? error.message : "The map catalogue could not be read." };
  }
}

/**
 * Whether a graph URL can actually be loaded by the engine host.
 *
 * The engine runs inside a page served over HTTPS — on Android that is
 * `https://appassets.androidplatform.net`, on the web it is the app's own
 * origin. A plain-`http://` graph is therefore **blocked as mixed
 * content**, and the only trace is a console warning nobody reads: the
 * graph silently never loads, the app falls back to whatever it had, and
 * the first visible symptom is a driver's phone refusing a real job for
 * disagreeing about an epoch. That is exactly how it presented.
 *
 * So an http URL is refused where it is configured rather than warned
 * about where it is fetched. Loopback is exempt because a developer
 * serving a graph from their own machine is the one case browsers permit
 * — and it is the case the emulator default relies on.
 */
export function graphUrlProblem(url: string): string | null {
  const text = String(url ?? "").trim();
  if (!text) return "A region needs a URL.";
  let parsed: URL;
  try {
    parsed = new URL(text);
  } catch {
    return "That is not a URL.";
  }
  if (parsed.protocol === "https:") return null;
  if (parsed.protocol !== "http:") return `A graph is served over https, not ${parsed.protocol}`;
  const host = parsed.hostname;
  const loopback = host === "localhost" || host === "127.0.0.1" || host === "10.0.2.2" || host === "::1";
  if (loopback) return null;
  return (
    `A graph served over http cannot be loaded (${host}). The engine page is https, so the browser `
    + "blocks it as mixed content and the map silently never arrives. Serve it over https."
  );
}
